import { useEffect, useState } from "react";
import Header from "../component/menu/Header";
import { constants, DISCOUNT_TYPES } from "../constant/constants";
import SideMenu from "../component/menu/SideMenu";
import CustomLoader from "../component/menu/CustomLoader";
import { useNavigate, useParams } from "react-router-dom";
import ApiService from "../utils/ApiService";
import useUserStore from "../stores/store";
import OrderFormHeaderComponent from "../component/OrderFormHeaderComponent";
import OrderPaymentItemsComponent from "../component/restaurants/orders/OrderPaymentItemsComponent";
import OrderPaymentFormComponent from "../component/restaurants/orders/OrderPaymentFormComponent";
import { formatAsOrderListFromOrderDetails, redirectToTargetPage } from "../services/order-common-service";
import {
  defaultSummaryData,
  getCalculatedPriceSummary,
  getFormatedFood,
  sendSplitBillReq,
  toggleFoodSelection,
  validateSplitBillData,
} from "../services/order-payment-service";
import { processAndSendPrintCommand, validateLoyaltyDiscount } from "../services/order-processing-service";
import { calculatePriceSummary, sdObjDefault, subtotalObjDefault } from "../services/order-split-service";

const discountTypes = [DISCOUNT_TYPES[1], DISCOUNT_TYPES[0]];

export default function OrderPaymentSplit() {
  const navigate = useNavigate();
  const { user, showNotification, setNetworkCall } = useUserStore();
  const params = useParams();
  const { orderId } = params;

  const [defaultDiscountTypes, setDefaultDiscountTypes] = useState(discountTypes);
  const [orderRecord, setOrderRecord] = useState({ discountApplicablePrice: 0 });
  const [foods, setFoods] = useState([]);
  const [summary, setSummary] = useState({ ...defaultSummaryData });

  const updateOrderRecord = (foodPriceBeforeDiscount, totalPriceBeforeDiscount, discountApplicablePrice, subtotalObj, sdObj) => {
    setOrderRecord((prev) => ({
      ...prev,
      foodPriceBeforeDiscount: foodPriceBeforeDiscount,
      totalPriceBeforeDiscount: totalPriceBeforeDiscount,
      discountApplicablePrice,
      subtotalObj,
      sdObj
    }));
  };

  const onUpdateFoodSelection = (uuid) => {
    const mFoods = toggleFoodSelection(uuid, foods);
    setFoods(mFoods);
    
    const calcValues = calculatePriceSummary(
      mFoods,
      orderRecord.isVatIncluded,
      orderRecord.isSupplementaryDutyIncluded,
      orderRecord.isServiceChargeIncluded,
      orderRecord.vatPercent,
      orderRecord.serviceChargePercent
    );
    updateOrderRecord(
      calcValues.foodPriceBeforeDiscount,
      calcValues.totalPriceBeforeDiscount,
      calcValues.discountApplicablePrice,
      calcValues.subtotalObj,
      calcValues.sdObj
    );
    setSummary({
      subtotal: calcValues.subtotal,
      sd: calcValues.sd,
      serviceChargePercent: orderRecord.serviceChargePercent,
      serviceCharge: calcValues.serviceCharge,
      vatPercent: orderRecord.vatPercent,
      vat: calcValues.vat,
      grossTotal: calcValues.grossTotal,
    });

    if (calcValues.discountApplicablePrice === 0) {
      setDefaultDiscountTypes([DISCOUNT_TYPES[0]]);
    } else {
      setDefaultDiscountTypes(discountTypes);
    }
  };

  async function getOrder(orderId) {
    setNetworkCall(true);
    const token = user.token.token;
    const res = await ApiService.FETCH_SPLITTED_DETAILS(orderId, token);

    setNetworkCall(false);

    if (res.code !== 200) {
      showNotification("Failed to fetch order details.");
      return null;
    }

    const formatedData = formatAsOrderListFromOrderDetails({...res.payload.record, subtotalObj: subtotalObjDefault, sdObj: sdObjDefault});
    const formatedFoods = getFormatedFood(res.payload.record.foods);
    setFoods(formatedFoods);
    setOrderRecord({
      ...formatedData,
      foodPriceBeforeDiscount: 0,
      totalPriceBeforeDiscount: 0,
      discountApplicablePrice: 0,
    });

    if (formatedData.discountApplicablePrice === 0) {
      setDefaultDiscountTypes([DISCOUNT_TYPES[0]]);
    }
  }

  async function submitBillSplittedData(params) {
    try {
      const storeData = { ...params.roundedValues, isRoundingApplicable: params.isRoundingApplicable };
      localStorage.setItem("paymentReceiveRoundedValues", JSON.stringify(storeData));
      const error = validateLoyaltyDiscount(params.discountType, params.discount, params.loyaltyMembershipInfo);

      if (error) {
        showNotification(error);
        return;
      }

      const errMsg = validateSplitBillData(params, foods);

      if (errMsg) {
        showNotification(errMsg);
        return;
      }

      setNetworkCall(true);

      const result = await sendSplitBillReq(orderRecord, { ...params }, foods, summary.grossTotal, user.token.token);

      if (!result.success) {
        showNotification(result.message);
        return;
      }

      // Auto print invoice
      processAndSendPrintCommand({ orderId: result.record.paidOrderId }, user.token.token, showNotification, 3, false);

      showNotification("Order Splitted successfully", false);
      navigate("/table");
    } catch (err) {
    } finally {
      setNetworkCall(false);
    }
  }

  const onActionTakenPaymentButtons = (actionType, params) => {
    if (["PAYMENT_DONE", "PAYMENT_CANCEL", "GUEST_BILL"].includes(actionType)) {
      redirectToTargetPage(navigate);
      return;
    }

    if (actionType === "SPLIT_BILL") {
      submitBillSplittedData(params);
    }
  };

  useEffect(() => {
    getOrder(orderId);
  }, [orderId]);

  return (
    <div className="flex flex-col bg-[#e5e7eb]">
      <CustomLoader />
      <Header title={""} menu={constants.MENU.ORDER} currentMenuKey={"T_4_S_4"} />
      <div className={`flex flex-row bg-prewhite`}>
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.ORDER} />
        <div className="flex flex-row p-[24px] w-full mt-[80px] ml-[80px]">
          <div className="flex flex-col w-full">
            <OrderFormHeaderComponent
              context={"PAYMENT"}
              title={`Payment for Order ${Boolean(orderRecord.orderNumber) ? `(${orderRecord.orderNumber})` : ""}`}
            />
            <div className="">
              <div className="flex gap-8 items-stretch">
                {Object.keys(orderRecord).length > 10 && (
                  <OrderPaymentFormComponent
                    order={orderRecord}
                    onPaymentParentActionHandler={onActionTakenPaymentButtons}
                    defaultDiscountTypes={defaultDiscountTypes}
                  />
                )}

                <OrderPaymentItemsComponent
                  context={"SPLIT_BILL"}
                  summary={summary}
                  foods={foods}
                  onUpdateFoodSelection={onUpdateFoodSelection}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
